import {
  CONFIGS,
  GetAdditionalFiltersOptions,
  useConfigFilters,
} from '@buildwise/libs/configurations';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import { ProjectType } from '@buildwise/pages/projects/hooks/useProjectsQuery';
import { Box, Typography } from '@mui/material';
import { ClearFiltersOptions, FilterType } from 'components/common/filter/Filter';
import useUserData from 'hooks/useUserData';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import StarFillIcon from 'remixicon-react/StarFillIcon';
import { handlePageScrollPosition } from 'utility/helpers';

function useProjectsFilters({ projects }: { projects: ProjectType[] }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showStarredProjects = useMemo(
    () => searchParams.get('showStarredProjects') === 'true',
    [searchParams],
  );
  const { pluralProjectsModuleName } = useBuildWiseModules();
  const { companyId } = useUserData();
  const getAdditionalFilters = useCallback(
    ({ configFilters }: GetAdditionalFiltersOptions): FilterType[] => {
      return [
        ...configFilters.map((filter, index) => ({
          ...filter,
          categoryIndex: index,
        })),
      ];
    },
    [pluralProjectsModuleName, showStarredProjects],
  );
  const { filters, isFiltersLoading, setFilters } = useConfigFilters({
    companyId,
    configName: CONFIGS.PROJECT_FILTERS.configName,
    data: projects,
    showFiltersCount: true,
    getAdditionalFilters,
  });

  const clearFilters = (options?: ClearFiltersOptions) => {
    if (options?.defaultClearFiltersCallback) {
      options?.defaultClearFiltersCallback?.();
    } else {
      setFilters((prevState) => {
        return prevState.map((filter) => ({
          ...filter,
          selectedOptions: [],
          selectedValue: filter.defaultSelectedValue,
          defaultSelectedValue: filter.defaultSelectedValue,
        }));
      });
    }

    setSearchParams(
      (prevState) => {
        prevState.delete('showStarredProjects');
        return prevState;
      },
      { replace: true },
    );
  };

  return {
    filters,
    isFiltersLoading,
    clearFilters,
    setFilters,
  };
}

export default useProjectsFilters;
